import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/networkHandler";
export const tokenConfig = (getState) => {
  const token = getState().auth.accessToken;

  const config = {
    headers: {},
  };

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
};

//Create Campaign
export const createCampaign = createAsyncThunk(
  "campaign/create",
  async (form, { getState, rejectWithValue }) => {
    try {
      const config = tokenConfig(getState);
      const response = await axios.post("/campaign/create", form, config);
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch all campaigns
export const getAllCampaigns = createAsyncThunk(
  "campaign/getAll",
  async (
    { page, rowsPerPage, name, campaignObj },
    { getState, rejectWithValue }
  ) => {
    try {
      const config = tokenConfig(getState);
      let params = `page=${page + 1}&limit=${rowsPerPage}`;
      if (name) {
        params += `&name=${encodeURIComponent(name)}`;
      }
      if (campaignObj) {
        params += `&campaignObj=${encodeURIComponent(campaignObj)}`;
      }
      const response = await axios.get(`/campaign?${params}`, config);
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch top campaigns
export const getTopCampaigns = createAsyncThunk(
  "campaign/getTopCampaigns",
  async (
    { page, rowsPerPage, name, campaignObj },
    { getState, rejectWithValue }
  ) => {
    try {
      const config = tokenConfig(getState);
      let params = `page=${page + 1}&limit=${rowsPerPage}`;
      if (name) {
        params += `&name=${encodeURIComponent(name)}`;
      }
      if (campaignObj) {
        params += `&campaignObj=${encodeURIComponent(campaignObj)}`;
      }
      const response = await axios.get(`/dashboard/top-campaigns?${params}`, config);
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch campaign by ID
export const getCampaignById = createAsyncThunk(
  "campaign/getById",
  async (campaignId, { getState, rejectWithValue }) => {
    try {
      const config = tokenConfig(getState);
      const response = await axios.get(`/campaign/${campaignId}`, config);
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Update campaign
export const updateCampaign = createAsyncThunk(
  "campaign/update",
  async ({ campaignId, form }, { getState, rejectWithValue }) => {
    try {
      const config = tokenConfig(getState);
      const response = await axios.put(`/campaign/${campaignId}`, form, config);
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Campaign Payment
export const createCampaignPayment = createAsyncThunk(
  "campaign/billing",
  async (form, { getState, rejectWithValue }) => {
    try {
      const config = tokenConfig(getState);
      const response = await axios.post("/campaign/billing", form, config);
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Get Bill
export const getBill = createAsyncThunk(
  "/campaign/company-account",
  async (_, { getState, rejectWithValue }) => {
    try {
      const config = tokenConfig(getState);
      const response = await axios.get("/campaign/company-account", config);
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

const campaignSlice = createSlice({
  name: "campaign",
  initialState: {
    loading: false,
    campaigns: [],
    topCampaigns: [],
    budget: null,
    userId: null,
    campaignId: null,
    bill: [],
    campaign: null,
    meta: null,
    success: null,
    error: null,
    createSuccess: false,
  },
  extraReducers: (builder) => {
    builder
      //create-campaign
      .addCase(createCampaign.pending, (state) => {
        state.loading = true;
        state.success = null;
        state.error = null;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
        state.createSuccess = true;
        state.budget = action.payload?.data.budget;
        state.userId = action.payload?.data.userId;
        state.campaignId = action.payload?.data.campaignId;

        localStorage.setItem(
          "campaignBudget",
          JSON.stringify(action.payload?.data.budget)
        );
        localStorage.setItem(
          "userId",
          JSON.stringify(action.payload?.data.userId)
        );
        localStorage.setItem(
          "campaignId",
          JSON.stringify(action.payload?.data.campaignId)
          );

        //console.log(state.budget)
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.loading = false;
        state.createSuccess = false;
        state.error = action.payload?.message || "An error occurred";
      })
      //get-all-campaign
      .addCase(getAllCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload.data;
        state.meta = action.payload.meta; // Store pagination meta
      })
      .addCase(getAllCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch campaigns";
      })
      // get-top-campaigns
      .addCase(getTopCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTopCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.topCampaigns = action.payload.data;
        state.meta = action.payload.meta; // Store pagination meta
      })
      .addCase(getTopCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch top campaigns";
      })
      //get-campaign-by-id
      .addCase(getCampaignById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCampaignById.fulfilled, (state, action) => {
        state.loading = false;
        state.campaign = action.payload.data;
      })
      .addCase(getCampaignById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch campaign";
      })
      //update-campaign
      .addCase(updateCampaign.pending, (state) => {
        state.loading = true;
        state.success = null;
        state.error = null;
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
        state.budget = action.payload?.data.budget;

        localStorage.setItem(
          "campaignBudget",
          JSON.stringify(action.payload?.data.budget)
        );
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update campaign";
      })
      //create-payment
      .addCase(createCampaignPayment.pending, (state) => {
        state.loading = true;
        state.success = null;
        state.error = null;
      })
      .addCase(createCampaignPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
        state.budget = 0;
        localStorage.setItem("campaignBudget", JSON.stringify(0));
      })
      .addCase(createCampaignPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "An error occurred";
      })
      //get-bill
      .addCase(getBill.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBill.fulfilled, (state, action) => {
        state.loading = false;
        state.bill = action.payload.data;
      })
      .addCase(getBill.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch bill";
      });
  },
});

export default campaignSlice.reducer;
